import {
  Card,
  CardActionArea,
  Divider,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  useTheme,
} from "@mui/material";
import { DeleteForever, DeleteOutline, MoreVert } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import * as dayjs from "dayjs";
import { ReactComponent as Arrow } from "../../../assets/easyReport/iconSvg/arrow.svg";
import useStylesCard from "./styles";

interface AgendamentoGroup {
  groupId: string;
  groupName: string;
}

interface Agendamento {
  id: string;
  titulo: string;
  perfil: string[];
  items: string[];
  group?: AgendamentoGroup[];
  dataInicial: string;
  dataFinal: string;
}
interface CardAgendamentoProps extends Agendamento {
  setDeleteModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedId?: React.Dispatch<React.SetStateAction<string | null>>;
  editMode?: boolean;
  deleteArray?: string[];
  setDeleteArray?: React.Dispatch<React.SetStateAction<string[]>>;
  inGroup: boolean;
}
export default function CardAgendamento({
  setDeleteModal,
  setSelectedId,
  editMode,
  deleteArray,
  setDeleteArray,
  inGroup,
  ...cardData
}: CardAgendamentoProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);

  const theme = useTheme();
  const open = Boolean(anchorEl);
  const cardAreaRef = useRef<HTMLButtonElement>(null);
  const styles = useStylesCard(!!editMode);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleDelete = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    setSelectedId?.(id);
    setDeleteModal?.(true);
    setAnchorEl(null);
  };

  const handleResize = () => {
    if (cardAreaRef.current) {
      const { offsetWidth } = cardAreaRef.current;
      setBoxWidth(offsetWidth);
    }
  };
  const handleDeleteToGroup = (id: string) => {
    const newArray = [...(deleteArray ?? [])];
    newArray.push(id);
    setDeleteArray?.(newArray);
  };

  const perfisVisiveis = useMemo(() => {
    if (!boxWidth) return [];

    const quantidade = Math.floor(boxWidth / 120);
    return cardData.perfil.slice(0, quantidade);
  }, [boxWidth, cardData.perfil]);
  return (
    <Card
      sx={{
        ...styles.card,
        borderColor: "primary",
        flexGrow: 1,
        width: inGroup ? "350px" : "100%",
        maxWidth: inGroup ? "350px" : "520px",
      }}
      elevation={0}
    >
      {editMode && (
        <Tooltip title={"Clique para remover do grupo"} placement="top" arrow>
          <CardActionArea
            sx={styles.deleteCardAction}
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteToGroup(cardData?.id);
            }}
          >
            <DeleteForever sx={styles.deleteIcon} color={"primary"} />
          </CardActionArea>
        </Tooltip>
      )}
      <CardActionArea
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          alignItems: "flex-start",
          width: "100%",
          opacity: editMode ? 0.15 : 1,
        }}
        onClick={() => navigate(`/easyReportV2/agendamento/${cardData.id}`)}
        disableRipple
        focusRipple={false}
        ref={cardAreaRef}
      >
        <Stack sx={styles.containerTitles}>
          <Tooltip title={cardData.titulo}>
            <Typography sx={{ ...styles.title }}>{cardData.titulo}</Typography>
          </Tooltip>
        </Stack>

        <Stack sx={styles.containerChip}>
          {perfisVisiveis.map((title, idx) => (
            <Tooltip title={cardData.perfil.join(",")}>
              <Chip
                key={idx}
                label={title}
                sx={{
                  borderRadius: "7px",
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  backgroundColor: theme.palette.background.cardProfile,
                  padding: "0px 6px",
                  height: "28px",
                  maxWidth: "100px",
                  marginRight: "4px",
                  "& .MuiChip-label": {
                    padding: 0,
                  },
                }}
              />
            </Tooltip>
          ))}
          {cardData.perfil.length > perfisVisiveis.length && (
            <Typography sx={{ textWrap: "nowrap" }}>
              + {cardData.perfil.length - perfisVisiveis.length}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ width: "100%" }} orientation="horizontal" />

        <Stack sx={styles.containerItens}>
          <Typography sx={styles.itens} fontWeight={500}>
            {"Itens"}
          </Typography>
          <Tooltip title={cardData.items.join(", ")}>
            <Typography sx={styles.itens}>
              {cardData.items.join(", ")}
            </Typography>
          </Tooltip>
        </Stack>
        <Stack sx={styles.containerDate}>
          <Stack>
            <Typography sx={styles.dateTitle}>{"Data inicial"}</Typography>
            <Typography sx={styles.date}>
              {dayjs(cardData.dataInicial).format("DD/MM/YYYY")}
            </Typography>
          </Stack>
          <Arrow
            style={{
              fillOpacity: 0,
              width: 24,
              height: 24,
              stroke: theme.palette.primary.main,
            }}
          />
          <Stack>
            <Typography sx={styles.dateTitle}>{"Data final"}</Typography>
            <Typography sx={styles.date}>
              {dayjs(cardData.dataFinal).format("DD/MM/YYYY")}
            </Typography>
          </Stack>
        </Stack>
      </CardActionArea>
      {!inGroup && (
        <IconButton
          aria-label="more"
          aria-controls={open ? "card-menu" : undefined}
          aria-haspopup="true"
          onClick={handleMenuClick}
          size="small"
          sx={styles.iconButton}
        >
          <MoreVert />
        </IconButton>
      )}
      <Menu
        id="card-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          sx={{ display: "flex", gap: 1 }}
          onClick={(e) => handleDelete(e, cardData.id)}
        >
          <DeleteOutline color="error" />
          Excluir agendamento
        </MenuItem>
      </Menu>
    </Card>
  );
}
